/**
 * GameResultCard — single past-result card for a perspective team.
 *
 * Replaces the private `GameCard` inside `IhsLast5Games.tsx` and the
 * `CompactRow` inside `IhsLast5GamesCompact.tsx`. The `density` prop
 * switches between the two layouts.
 *
 *   density='comfortable' (default)
 *     Multi-line card — competition + date header, home/score-pill/away
 *     row, optional scorers footer. Used on /match pages.
 *
 *   density='compact'
 *     Single-line row — H/A venue marker, opponent name, score-pill,
 *     date. Used inside 9:16 Stories overlays.
 *
 * Shape:
 *   { game: IhsLastGameEntry; perspectiveTeamName: string;
 *     market: { locale: string; timezone: string };
 *     density?: 'comfortable' | 'compact' }
 *
 * i18n + market locale are passed in explicitly so the primitive stays
 * pure (no hook calls).
 */

import type { ReactElement } from 'react';
import { z } from 'zod';
import type { PrimitiveMeta } from './_meta.js';
import { dateNumericShort } from '../../utils/datetime.js';
import type { IhsLastGameEntry } from '../../types/ihs.js';
import { ScorePill } from './ScorePill.js';
import styles from './GameResultCard.module.css';

/**
 * Mirrors `IhsLastGameEntry` from `types/ihs.ts` plus the
 * market/perspective wrap props. The engine ships these shapes wholesale
 * from `cms.v_*` views, so the schema is the same shape the engine
 * already validates server-side. Kept inline here (not imported from a
 * shared schema) to keep the primitive free of cross-layer imports.
 */
const dataShape = z.object({
  game: z.object({
    sportEventUrn: z.string().nullable(),
    date: z.string(), // YYYY-MM-DD
    competitionName: z.string(),
    competitionId: z.string().nullable(),
    stageLabel: z.string().nullable(),
    perspective: z.enum(['home', 'away']),
    scoreLine: z.string(),
    outcome: z.enum(['W', 'D', 'L']),
    homeTeamName: z.string(),
    awayTeamName: z.string(),
    homeScorers: z.array(z.string()),
    awayScorers: z.array(z.string()),
    penaltyShootout: z.string().nullable(),
  }),
  perspectiveTeamName: z.string(),
  market: z.object({
    locale: z.string(),
    timezone: z.string(),
  }),
  density: z.enum(['comfortable', 'compact']).optional(),
  venueHomeShort: z.string().optional(),
  venueAwayShort: z.string().optional(),
  venueHomeAria: z.string().optional(),
  venueAwayAria: z.string().optional(),
});

export const META: PrimitiveMeta = {
  name: 'game-result-card',
  description:
    'Past-result card — competition/date header, score-pill, optional scorers.',
  composable: true,
  dataShape,
  densities: ['standard', 'compact'],
  breakpoints: { mobile: true, wide: true },
  animation: 'reveal',
  composes: ['score-pill'],
};

interface MarketContext {
  locale: string;
  timezone: string;
}

interface GameResultCardProps {
  game: IhsLastGameEntry;
  perspectiveTeamName: string;
  market: MarketContext;
  density?: 'comfortable' | 'compact';
  /** Compact-only — single-letter venue marker (e.g. 'H' / 'A'). */
  venueHomeShort?: string;
  venueAwayShort?: string;
  /** Compact-only — full aria labels behind the H/A markers. */
  venueHomeAria?: string;
  venueAwayAria?: string;
}

export function GameResultCard({
  game,
  perspectiveTeamName,
  market,
  density = 'comfortable',
  venueHomeShort = 'H',
  venueAwayShort = 'A',
  venueHomeAria = 'Home',
  venueAwayAria = 'Away',
}: GameResultCardProps): ReactElement {
  const isHomePerspective = game.homeTeamName === perspectiveTeamName;

  if (density === 'compact') {
    const opponent = isHomePerspective ? game.awayTeamName : game.homeTeamName;
    const venuePrefix = isHomePerspective ? venueHomeShort : venueAwayShort;
    const venueAria = isHomePerspective ? venueHomeAria : venueAwayAria;
    return (
      <div className={`${styles.compactRow} stage-row`}>
        <span className={styles.venue} aria-label={venueAria}>
          {venuePrefix}
        </span>
        <span className={styles.compactOpponent}>{opponent}</span>
        <ScorePill scoreLine={game.scoreLine} outcome={game.outcome} size="sm" />
        <span className={styles.compactDate}>
          {dateNumericShort(game.date, market.locale, market.timezone)}
        </span>
      </div>
    );
  }

  return (
    <article className={styles.card}>
      <header className={styles.cardHead}>
        <span className={styles.compLine}>
          <span className={styles.compPlus} aria-hidden="true">+</span>
          {game.competitionName}
          {game.stageLabel ? ` - ${game.stageLabel}` : ''}
        </span>
        <span className={styles.date}>
          {dateNumericShort(game.date, market.locale, market.timezone)}
        </span>
      </header>
      <div className={styles.matchup}>
        <span
          className={`${styles.team} ${styles.teamLeft}${isHomePerspective ? ` ${styles.teamPersp}` : ''}`}
        >
          {game.homeTeamName}
        </span>
        <ScorePill scoreLine={game.scoreLine} outcome={game.outcome} size="md" />
        <span
          className={`${styles.team} ${styles.teamRight}${!isHomePerspective ? ` ${styles.teamPersp}` : ''}`}
        >
          {game.awayTeamName}
        </span>
      </div>
      {game.penaltyShootout && (
        <div className={styles.shootout}>{game.penaltyShootout}</div>
      )}
      {(game.homeScorers.length > 0 || game.awayScorers.length > 0) && (
        <div className={styles.scorers}>
          <span className={styles.scorersHome}>
            {game.homeScorers.join(', ')}
          </span>
          <span className={styles.scorersIcon} aria-hidden="true">
            {String.fromCodePoint(0x26bd) /* football */}
          </span>
          <span className={styles.scorersAway}>
            {game.awayScorers.join(', ')}
          </span>
        </div>
      )}
    </article>
  );
}

export default GameResultCard;
